// src/components/corkboard/CorkAddCluster.jsx
//
// The three floating "+" buttons in the corner of the board — add an actor, a
// scene or an artifact.
//
// They are fixed to the viewport rather than placed in the toolbar because the
// board scrolls: an add button that scrolled away with the cork would be gone
// exactly when an author had scrolled to the empty patch they wanted to fill.
//
// Clicking one does not create anything. It opens the create form in the right
// rail, which is where the name, description and first relationship are
// chosen — so a new card arrives on the board already connected, rather than as
// an untitled square the author then has to find and describe.
import React from 'react';
import { Box, Tooltip } from '@mui/material';

import { CB, FONT, TYPE_COLOR, TYPE_LABEL, BP, DOCKED_PANEL_W } from './corkTokens';

// Bottom-up, so the one an author reaches for most sits nearest the corner.
const ADDABLE = ['artifact', 'scene', 'actor'];

export default function CorkAddCluster({ onAdd, activeType, panelOpen }) {
  return (
    <Box sx={{
      // Two offsets, both of them avoiding something.
      //
      // bottom:96, not 24, clears the Debug Panel — App.js mounts it at
      // `fixed; bottom:16; right:16; zIndex:1000` in development, directly on
      // top of the lowest button here. Not mounted in production, so this costs
      // a visually irrelevant 72px there and makes "Add actor" clickable for
      // anyone working locally, which is everyone building this page.
      //
      // right clears the details panel above 900px, where it is docked at
      // DOCKED_PANEL_W. These are controls for the board; parked on the panel
      // they sit on top of the text you are reading. Below 900 the panel floats
      // away to the edge, so the corner is free again — except for the handle,
      // which is why this is 24 and not 0.
      position: 'fixed', right: 24, bottom: 96, zIndex: 200,
      [BP.docked]: { right: DOCKED_PANEL_W + 24 },
      // Below 900 the panel floats over that same corner, so an open panel
      // would wear these buttons across its description field. They add to the
      // board, and while the panel is open the board is what you are not
      // looking at — so they go, and the handle brings them back with it.
      [BP.panelFloats]: { display: panelOpen ? 'none' : 'flex' },
      display: 'flex', flexDirection: 'column', gap: 1.25, alignItems: 'flex-end',
    }}>
      {ADDABLE.map((type) => (
        <Tooltip key={type} title={`Add ${(TYPE_LABEL[type] || type).toLowerCase()}`} placement="left">
          <Box
            component="button"
            type="button"
            aria-label={`Add ${(TYPE_LABEL[type] || type).toLowerCase()}`}
            aria-pressed={activeType === type}
            onClick={() => onAdd?.(type)}
            sx={{
              width: 46, height: 46, borderRadius: '50%', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontFamily: FONT.serif, fontSize: 20, fontWeight: 700, lineHeight: 1,
              color: CB.white,
              backgroundColor: TYPE_COLOR[type],
              border: activeType === type ? `2px solid ${CB.white}` : 'none',
              boxShadow: '0 10px 22px -8px rgba(20,10,30,0.55)',
              transition: 'transform .18s ease, box-shadow .18s ease',
              '&:hover': {
                transform: 'scale(1.08)',
                boxShadow: '0 14px 26px -8px rgba(20,10,30,0.6)',
              },
            }}
          >
            +
          </Box>
        </Tooltip>
      ))}
    </Box>
  );
}
